import { supabase } from './supabase';

export interface CatalogueEntry {
  id: string;
  code: string;
  nom: string;
  categorie?: string;
}

export function normalizeName(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[()\[\].,;:!?'"\-_+/=*]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Mapping déterministe des libellés du checklist médecin vers des mots-clés
 * du catalogue. On ne fait PLUS de matching par préfixe ni de recherche floue :
 * - un tableau de mots-clés = équivalent(s) connu(s) dans le catalogue
 * - un tableau vide = aucune analyse de labo équivalente → sera créée
 *   automatiquement dans la catégorie "Analyse générale" (tri par le labo)
 */
const LABEL_KEYWORDS: Record<string, string[]> = {
  'nfs numeration formule sanguine': ['numeration formule', 'nfs'],
  'creat creatinine': ['creatinine'],
  'calcemie': [],
  'mg magnesium': ['magnesium'],
  'ionogramme': ['ionogramme'],
  'chol t cholesterol total': ['cholesterol total'],
  'hdl': ['hdl'],
  'ldl': ['ldl'],
  'transa transaminases': ['asat', 'alat', 'tgo', 'tgp', 'transaminase'],
  'trigly triglycerides': ['triglyceride'],
  'hiv': ['vih', 'hiv'],
  'aghbs': ['antigene hbs', 'hepatite b', 'aghbs'],
  'urice acide urique': ['acide urique'],
  'gly glycemie': ['glycemie'],
  'tpha': ['tpha'],
  'vdrl': ['vdrl'],
  'ge dp': ['goutte epaisse', 'frottis'],
  'ecbu': ['ecbu', 'cytobacteriologique'],
  'crp': ['crp', 'proteine c reactive'],
  'cpk': ['cpk', 'creatine phosphokinase'],
  'aslo': ['aslo', 'antistreptolysine'],
  'fs dp': ['frottis', 'goutte epaisse'],
  'fs': ['frottis'],
  'uree': ['uree'],
  'gamma gt': ['gamma gt', 'ggt'],
  'tp inr': ['taux de prothrombine', 'inr'],
  'vs': ['vitesse de sedimentation'],
  'd dimeres': ['d dimere'],
  'tsh': ['tsh'],
  'electrophorese hb': ['electrophorese', 'hemoglobine'],
  'glucosurie': ['glucosurie'],
  'ts': ['temps de saignement'],
  'tck': ['cephaline', 'tca'],

  // Acronymes / termes sans équivalent labo → créés en "Analyse générale"
  'tc': [],
  'te': [],
  'sdw': [],
  'tbg': [],
  'akop': [],
  'atb': [],
  'icg': [],
  'gsrh': [],

  // Échographies / imagerie → créées en "Analyse générale" (le labo les triera)
  'pelvienne': [],
  'vesico prostatique': [],
  'obstetricale': [],
  'abdominale': [],
  'abdomino pelvienne': [],
  'testiculaire': [],
  'doppler cardiaque': [],
  'renale': [],
  'doppler veineux': [],
  'hepatique': [],
  'des parties molles': [],
  'mammaires': [],
  'thyroidienne': [],
  'cervicale': [],
  'ecg': [],
  'transfontanelle': [],
};

function getKeywordsForLabel(label: string): string[] | null {
  const norm = normalizeName(label);
  if (!norm) return null;
  if (Object.prototype.hasOwnProperty.call(LABEL_KEYWORDS, norm)) {
    return LABEL_KEYWORDS[norm];
  }
  return null;
}

export function matchCatalogueIds(
  label: string,
  catalogue: CatalogueEntry[],
): string[] {
  const normLabel = normalizeName(label);
  if (!normLabel || !catalogue.length) return [];

  // 1. Nom exact
  const exactNom = catalogue.filter((e) => normalizeName(e.nom) === normLabel);
  if (exactNom.length) return exactNom.map((e) => e.id);

  // 2. Code exact (acronyme)
  const exactCode = catalogue.filter(
    (e) => normalizeName(e.code || '') === normLabel,
  );
  if (exactCode.length) return exactCode.map((e) => e.id);

  // 3. Correspondance déterministe du checklist (mots-clés exacts uniquement)
  const keywords = getKeywordsForLabel(label);
  if (keywords && keywords.length > 0) {
    const matched = catalogue.filter((e) => {
      const nomNorm = normalizeName(e.nom);
      const codeNorm = normalizeName(e.code || '');
      return keywords.some(
        (kw) => nomNorm.includes(kw) || codeNorm.includes(kw),
      );
    });
    if (matched.length) return matched.map((e) => e.id);
  }

  return [];
}

export function matchAllCatalogueIds(
  typeAnalyse: string,
  catalogue: CatalogueEntry[],
): string[] {
  if (!typeAnalyse || !catalogue.length) return [];

  const labels = typeAnalyse
    .split(';')
    .map((l) => l.trim())
    .filter(Boolean);

  const allIds: string[] = [];
  const seen = new Set<string>();

  for (const label of labels) {
    for (const id of matchCatalogueIds(label, catalogue)) {
      if (!seen.has(id)) {
        seen.add(id);
        allIds.push(id);
      }
    }
  }

  return allIds;
}

async function createCatalogueEntry(
  nom: string,
  existingCodes: Set<string>,
  categorie: string = 'Analyse générale',
): Promise<CatalogueEntry | null> {
  // `code` est NOT NULL et UNIQUE dans la table — on doit absolument en fournir un,
  // sinon l'insert échouait silencieusement (d'où "Aucune analyse trouvée" côté labo).
  const base = nom
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 20) || 'ANALYSE';

  for (let attempt = 0; attempt < 8; attempt++) {
    let code = base;
    if (attempt > 0) code = `${base}_${attempt + 1}`;
    if (existingCodes.has(code)) continue;

    try {
      const { data, error } = await supabase
        .from('analyses_biochimie_catalogue')
        .insert({ code, nom, categorie })
        .select('id, code, nom, categorie')
        .single();

      if (!error && data) {
        existingCodes.add(code);
        return data as CatalogueEntry;
      }
      // Violation de contrainte UNIQUE (concurrence) → on tente le code suivant
      if (error?.code === '23505') continue;
      return null;
    } catch {
      return null;
    }
  }
  return null;
}

export interface ResolveResult {
  ids: string[];
  catalogue: CatalogueEntry[];
}

/**
 * Résout une liste de libellés d'analyse vers des IDs du catalogue.
 * Si un libellé n'existe pas (nom/code exact introuvable), il est créé
 * automatiquement dans la catégorie "Analyse générale". Le laboratoire
 * effectuera ensuite le tri/classement de ces nouvelles entrées.
 */
export async function resolveAnalysesToCatalogueIds(
  labels: string[],
  catalogue: CatalogueEntry[],
  categorie: string = 'Analyse générale',
): Promise<ResolveResult> {
  const ids: string[] = [];
  const seen = new Set<string>();
  let working = [...catalogue];
  const existingCodes = new Set<string>(
    catalogue.map((c) => (c.code || '').toUpperCase()).filter(Boolean),
  );

  for (const raw of labels) {
    const label = (raw || '').trim();
    if (!label) continue;

    let matches = matchCatalogueIds(label, working);
    if (matches.length === 0) {
      const created = await createCatalogueEntry(label, existingCodes, categorie);
      if (created) {
        matches = [created.id];
        working = [...working, created];
      }
    }

    for (const id of matches) {
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
  }

  return { ids, catalogue: working };
}